import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { formatTime } from '../../../utils/dateUtils';

const FormProgress = ({ 
  currentStep, 
  totalSteps, 
  onStepChange, 
  onSave, 
  onPreview, 
  onPublish,
  isSaving = false,
  hasUnsavedChanges = false,
  formData = {},
  errors = {}
}) => {
  const steps = [
    {
      id: 1,
      title: 'المعلومات الأساسية',
      description: 'الاسم والمدينة والشعار',
      icon: 'Info',
      requiredFields: ['nameAr', 'nameEn', 'cityId']
    },
    {
      id: 2,
      title: 'الوصف والمحتوى',
      description: 'الوصف ومعلومات الأعضاء والمحتوى الإضافي',
      icon: 'FileText',
      requiredFields: ['descriptionAr', 'totalMembers']
    }
  ];

  const getStepStatus = (step) => {
    const hasErrors = step?.requiredFields?.some(field => errors?.[field]);
    const isComplete = step?.requiredFields?.every(field => formData?.[field]);
    
    if (hasErrors) return 'error';
    if (isComplete) return 'complete';
    if (step?.id === currentStep) return 'current';
    return 'pending';
  };

  const getStepIcon = (step) => {
    const status = getStepStatus(step);
    
    switch (status) {
      case 'complete':
        return 'CheckCircle';
      case 'error':
        return 'XCircle';
      case 'current':
        return step?.icon;
      default:
        return step?.icon;
    }
  };

  const getStepColor = (step) => {
    const status = getStepStatus(step);
    
    switch (status) {
      case 'complete':
        return 'text-success';
      case 'error':
        return 'text-error';
      case 'current':
        return 'text-primary';
      default:
        return 'text-muted-foreground';
    }
  };

  const canProceedToNext = () => {
    const currentStepData = steps?.find(step => step?.id === currentStep);
    if (!currentStepData) return false;
    
    return currentStepData?.requiredFields?.every(field => formData?.[field] && !errors?.[field]);
  };

  const canPublish = () => {
    const allRequiredFields = steps?.flatMap(step => step?.requiredFields);
    return allRequiredFields?.every(field => formData?.[field] && !errors?.[field]);
  };

  const getCompletionPercentage = () => {
    const allRequiredFields = steps?.flatMap(step => step?.requiredFields);
    const completedFields = allRequiredFields?.filter(field => formData?.[field]);
    return Math.round((completedFields?.length / allRequiredFields?.length) * 100);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 space-y-6">
      {/* Progress Header */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground font-arabic">
            تقدم النموذج
          </h3>
          <span className="text-sm text-muted-foreground font-caption">
            {getCompletionPercentage()}% مكتمل
          </span>
        </div>
        
        {/* Progress Bar */}
        <div className="w-full bg-muted rounded-full h-2">
          <div 
            className="bg-primary h-2 rounded-full transition-all duration-300"
            style={{ width: `${getCompletionPercentage()}%` }}
          />
        </div>
      </div>
      {/* Steps */}
      <div className="space-y-3">
        {steps?.map((step, index) => {
          const status = getStepStatus(step);
          const isClickable = step?.id <= currentStep || status === 'complete';
          
          return (
            <div
              key={step?.id}
              className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors ${
                step?.id === currentStep 
                  ? 'bg-primary/10 border border-primary/20' :'hover:bg-muted/50'
              } ${!isClickable ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => isClickable && onStepChange(step?.id)}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                status === 'complete' ? 'bg-success text-success-foreground' :
                status === 'error' ? 'bg-error text-error-foreground' :
                status === 'current' ? 'bg-primary text-primary-foreground' :
                'bg-muted text-muted-foreground'
              }`}>
                <Icon name={getStepIcon(step)} size={16} />
              </div>
              <div className="flex-1">
                <h4 className={`text-sm font-medium ${getStepColor(step)} font-arabic`}>
                  {step?.title}
                </h4>
                <p className="text-xs text-muted-foreground font-caption">
                  {step?.description}
                </p>
              </div>
              {status === 'complete' && (
                <Icon name="Check" size={16} className="text-success" />
              )}
              {status === 'error' && (
                <Icon name="AlertCircle" size={16} className="text-error" />
              )}
            </div>
          );
        })}
      </div>
      {/* Navigation Buttons */}
      <div className="space-y-3 pt-4 border-t border-border">
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onStepChange(currentStep - 1)}
            disabled={currentStep === 1}
            iconName="ChevronLeft"
            iconPosition="left"
            iconSize={16}
            className="flex-1"
          >
            السابق
          </Button>
          
          <Button
            variant="default"
            size="sm"
            onClick={() => onStepChange(currentStep + 1)}
            disabled={currentStep === totalSteps || !canProceedToNext()}
            iconName="ChevronRight"
            iconPosition="right"
            iconSize={16}
            className="flex-1"
          >
            التالي
          </Button>
        </div>

        {/* Action Buttons */}
        <div className="space-y-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onSave}
            loading={isSaving}
            iconName="Save"
            iconPosition="left"
            iconSize={16}
            fullWidth
          >
            {isSaving ? 'جاري الحفظ...' : 'حفظ المسودة'}
          </Button>
          
          <Button
            variant="secondary"
            size="sm"
            onClick={onPreview}
            iconName="Eye"
            iconPosition="left"
            iconSize={16}
            fullWidth
          >
            معاينة
          </Button>
          
          <Button
            variant="default"
            size="sm"
            onClick={onPublish}
            disabled={!canPublish()}
            iconName="Send"
            iconPosition="left"
            iconSize={16}
            fullWidth
          >
            نشر الاتحاد الفرعي
          </Button>
        </div>
      </div>
      {/* Unsaved Changes Warning */}
      {hasUnsavedChanges && (
        <div className="bg-warning/10 border border-warning/20 rounded-lg p-3">
          <div className="flex items-center space-x-2">
            <Icon name="AlertTriangle" size={16} className="text-warning" />
            <p className="text-sm text-warning font-caption">
              لديك تغييرات غير محفوظة
            </p>
          </div>
        </div>
      )}
      {/* Auto-save Status */}
      <div className="text-xs text-muted-foreground text-center font-caption">
        آخر حفظ تلقائي: {formatTime(new Date())}
      </div>
    </div>
  );
};

export default FormProgress;